import {
  debounce,
  throttle,
  lockScroll,
  unlockScroll,
  releaseLockedScroll,
  activateDrawer,
  activateModal,
} from '@theme/coco-utilities';

const DEBOUNCE_MS = 300;
const MOBILE_FILTER_MQ = window.matchMedia('(max-width: 767px)');
const SIDEBAR_DESKTOP_MQ = window.matchMedia('(min-width: 1025px)');


class CocoCollectionFilters extends HTMLDivElement {
  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.boundTriggers = new Set();
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    if (!this.hasAttribute('data-state')) this.setAttribute('data-state', 'closed');


    if (this.getAttribute('data-state') === 'open') {
      this.classList.add('open-filters');
      this.classList.remove('sidebar-closed');
    } else {
      this.classList.add('sidebar-closed');
      this.classList.remove('open-filters');
    }

    this.syncTriggerState();
    this.syncFilterInert();
    this.bindTrigger();

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    SIDEBAR_DESKTOP_MQ.addEventListener?.('change', this.onMediaChange);
  }

  disconnectedCallback() {
    this.unbindTrigger();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    SIDEBAR_DESKTOP_MQ.removeEventListener?.('change', this.onMediaChange);

    const drawer = document.getElementById(`facets-drawer-${this.sectionId}`);
    const release = this.releaseDrawerModal || drawer?.releaseModal;

    if (drawer?.hasAttribute('active') && release) {
      drawer.releaseModal = release;
    } else {
      release?.();
      if (drawer) drawer.releaseModal = null;
    }
    this.releaseDrawerModal = null;

    this.releaseSidebarModal?.();
    this.releaseSidebarModal = null;
  }

  bindTrigger() {
    const triggers = this.triggers;
    if (!triggers.length) return;

    triggers.forEach((trigger) => {
      if (this.boundTriggers.has(trigger)) return;
      trigger.addEventListener('click', this.onTriggerClick);
      this.boundTriggers.add(trigger);
    });
  }

  rebindTriggers() {
    this.unbindTrigger();
    this.bindTrigger();
    this.syncTriggerState();
  }

  static rebindTriggersForSection(sectionId) {
    document
      .querySelector(`div[is="coco-collection-filters"][data-section-id="${sectionId}"]`)
      ?.rebindTriggers?.();
  }

  unbindTrigger() {
    this.boundTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTriggerClick);
    });
    this.boundTriggers.clear();
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.lastTrigger = event.currentTarget;
    this.toggle();
  }

  // ---- Mode + state ---------------------------------------------------------

  get filterStyle() {
    return this.dataset.filterStyle === 'drawer' ? 'drawer' : 'sidebar';
  }

  get useDrawer() {
    return this.filterStyle === 'drawer' || !SIDEBAR_DESKTOP_MQ.matches;
  }

  get isOpen() {
    return this.useDrawer ? this.drawer?.hasAttribute('active') === true : this.getAttribute('data-state') === 'open';
  }

  // ---- DOM lookups (id-based, scoped to this section) ----------------------

  get triggers() {
    return Array.from(
      document.querySelectorAll(`[data-facet-toggle][data-section-id="${this.sectionId}"]`)
    );
  }

  get trigger() {
    if (this.lastTrigger?.isConnected) return this.lastTrigger;
    // Prefer a visible trigger when restoring focus after close.
    return this.triggers.find((el) => el.offsetParent !== null) || this.triggers[0] || null;
  }

  get drawer() {
    return document.getElementById(`facets-drawer-${this.sectionId}`);
  }

  get drawerHost() {
    return document.querySelector(
      `#shopify-section-${this.sectionId} [data-facets-drawer-host]`
    );
  }

  get sidebar() {
    return this.querySelector(`#facets-sidebar-${this.sectionId}`);
  }

  // ---- Public API -----------------------------------------------------------

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.useDrawer ? this.openDrawer() : this.openSidebar();
  }

  close() {
    this.useDrawer ? this.closeDrawer() : this.closeSidebar();
  }

  // ---- Sidebar mode --------------------------------------------------------
  // Toggles the `open-filters` / `sidebar-closed` classes the CSS targets,
  // and mirrors the state on `data-state` for any selectors that prefer it.

  openSidebar() {
    this.classList.add('open-filters');
    this.classList.remove('sidebar-closed');
    this.setAttribute('data-state', 'open');
    this.syncTriggerState();
    this.syncFilterInert();
    // Sidebar is non-modal: the rest of the page must stay interactive, so we
    // skip the page-wide inert trap and only manage initial + restoration focus.
    this.releaseSidebarModal = activateModal(this.sidebar, {
      trap: false,
      initialFocus: (s) => s.querySelector('[is="close-drawer"]'),
    });
  }

  closeSidebar() {
    this.classList.remove('open-filters');
    this.classList.add('sidebar-closed');
    this.setAttribute('data-state', 'closed');
    this.syncTriggerState();
    this.releaseSidebarModal?.(this.trigger);
    this.releaseSidebarModal = null;
    this.syncFilterInert();
  }

  // Keep closed filter UI out of the tab order:
  // - desktop sidebar panel when closed
  // - data-facets-drawer-host / drawer when drawer is not open
  syncFilterInert() {
    this.syncSidebarInert();
    this.syncDrawerHostInert();
  }

  syncSidebarInert() {
    const sidebar = this.sidebar;
    if (!sidebar) return;

    const allowFocus =
      this.filterStyle === 'sidebar' &&
      SIDEBAR_DESKTOP_MQ.matches &&
      this.getAttribute('data-state') === 'open';

    sidebar.toggleAttribute('inert', !allowFocus);
    sidebar.setAttribute('aria-hidden', allowFocus ? 'false' : 'true');

    if (!allowFocus && sidebar.contains(document.activeElement)) {
      this.trigger?.focus?.({ preventScroll: true });
    }
  }

  syncDrawerHostInert() {
    const host = this.drawerHost;
    const drawer = this.drawer;
    const drawerOpen = Boolean(drawer?.hasAttribute('active'));

    // Tab into drawer content only while the drawer is open.
    if (host) {
      const drawerParkedInHost = Boolean(drawer && host.contains(drawer));
      const allowHostFocus = drawerOpen && drawerParkedInHost;
      host.toggleAttribute('inert', !allowHostFocus);
      host.setAttribute('aria-hidden', allowHostFocus ? 'false' : 'true');
    }

    if (!drawer) return;

    if (drawerOpen) {
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
    } else {
      drawer.toggleAttribute('inert', true);
      drawer.setAttribute('aria-hidden', 'true');

      if (drawer.contains(document.activeElement)) {
        this.trigger?.focus?.({ preventScroll: true });
      }
    }
  }

  // ---- Drawer mode (state via drawer [open]/[active] attributes) -----------

  openDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
    drawer.dataset.sectionId = this.sectionId;

    drawer.setAttribute('open', '');
    // Allow keyboard focus before paint; host stays inert as a parking slot.
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    this.drawerHost?.toggleAttribute('inert', true);
    this.drawerHost?.setAttribute('aria-hidden', 'true');

    drawer.setAttribute('data-scroll-locked', '');
    lockScroll(drawer);
    this.syncTriggerState();

    requestAnimationFrame(() => {
      if (!drawer.isConnected || !drawer.hasAttribute('open')) return;

      drawer.setAttribute('active', '');
      this.syncDrawerHostInert();

      // Store release on the drawer element so it survives section re-renders
      // (filter apply replaces <coco-collection-filters> but keeps the body drawer).
      drawer.releaseModal?.();
      drawer.releaseModal = activateDrawer(drawer, {
        initialFocus: (d) => d.querySelector('[is="close-drawer"]:not([data-fixed-overlay])'),
      });
      this.releaseDrawerModal = drawer.releaseModal;
    });
  }

  closeDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    const wasOpen = drawer.hasAttribute('active') || drawer.hasAttribute('open');
    if (!wasOpen) {
      releaseLockedScroll(drawer);
      return;
    }

    drawer.removeAttribute('active');
    this.syncTriggerState();
    this.syncDrawerHostInert();

    // Release trap + return focus to the trigger immediately so keyboard
    // users don't get stranded while the close transition plays.
    const release = drawer.releaseModal || this.releaseDrawerModal;
    if (release) {
      release(this.trigger);
    } else {
      // Fallback if the release fn was lost after a filter re-render — without
      // this, body children stay [inert] and Lenis stays stopped.
      CocoCollectionFilters.clearStaleBodyInert(drawer);
      this.trigger?.focus?.({ preventScroll: true });
    }
    drawer.releaseModal = null;
    this.releaseDrawerModal = null;

    // Match cart-drawer: wait for the slide/fade to finish before removing
    // [open], re-parenting, and unlocking scroll — avoids mid-close jank.
    let finished = false;
    const finish = () => {
      if (finished) return;
      if (drawer.hasAttribute('active')) return;
      finished = true;

      try {
        drawer.removeAttribute('open');

        if (drawer.parentElement === document.body) {
          const host = this.drawerHost;
          const collision = host?.querySelector(`#${CSS.escape(drawer.id)}`);
          if (host && !collision) host.appendChild(drawer);
          else if (!host?.contains(drawer)) drawer.remove();
        }
        this.syncDrawerHostInert();
      } finally {
        // Always restore Lenis / body scroll even if reparent throws.
        releaseLockedScroll(drawer);
        unlockScroll();
      }
    };

    const inner = drawer.querySelector('[data-drawer-inner]');
    if (inner) {
      const onTransitionEnd = (event) => {
        if (event.target !== inner) return;
        if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
        finish();
      };
      inner.addEventListener('transitionend', onTransitionEnd, { once: true });
    }
    window.setTimeout(finish, 600);
  }

  // Remove leftover [inert] from body children after a lost focus-trap release.
  static clearStaleBodyInert(keep) {
    for (const child of document.body.children) {
      if (child === keep || child.contains?.(keep)) continue;
      if (child.hasAttribute('inert')) child.removeAttribute('inert');
    }
  }

  // ---- Re-render preservation ----------------------------------------------

  // Ids of every open filter accordion. Captured + restored alongside focus
  // so that, when we move focus back onto a row inside an accordion that the
  // user had opened, the row is actually visible (otherwise the server-side
  // default open state would have collapsed it).
  static captureOpenAccordions() {
    const ids = new Set();
    document.querySelectorAll('details[is="accordion-details"][open]').forEach((d) => {
      if (d.id) ids.add(d.id);
    });
    return Array.from(ids);
  }

  static restoreOpenAccordions(ids) {
    if (!ids?.length) return;
    const set = new Set(ids);
    document.querySelectorAll('details[is="accordion-details"]').forEach((d) => {
      if (d.id && set.has(d.id)) d.setAttribute('open', '');
    });
  }

  // Snapshot of what currently has focus so we can restore it on the
  // equivalent element after the section is re-rendered. Keyed by stable
  // identifiers (input id / radio value) — class lists and DOM positions
  // are not stable across server re-renders.
  static captureFocus() {
    const active = document.activeElement;
    if (!active || active === document.body) return null;

    // Filter row (<li role="checkbox">): identified by its checkbox input id.
    if (active.matches?.('li[role="checkbox"]')) {
      const input = active.querySelector('input[type="checkbox"]');
      if (input?.id) return { type: 'filter-row', inputId: input.id };
    }

    // Sort row (<label role="radio">): identified by its radio value.
    if (active.matches?.('.facet-sorting-item')) {
      const input = active.querySelector('input[type="radio"]');
      if (input?.value) return { type: 'sort-row', value: input.value };
    }

    // Sort dropdown summary — natural landing spot after sort applies.
    if (active.closest?.('[data-facet-sort-form]') && active.tagName === 'SUMMARY') {
      const form = active.closest('[data-facet-sort-form]');
      return { type: 'sort-summary', formId: form?.id || null };
    }

    // Filter-toggle button (drawer / sidebar trigger).
    if (active.matches?.('[data-facet-toggle]')) {
      return { type: 'facet-toggle', sectionId: active.dataset.sectionId };
    }

    // Active filter "remove" pill — the pill itself disappears after removal,
    // so we record its href and fall back to clear-all / toggle on restore.
    if (active.matches?.('a[is="coco-facet-remove"]')) {
      const section = active.closest?.('[id^="shopify-section-"]');
      return {
        type: 'coco-facet-remove',
        href: active.getAttribute('href'),
        sectionId: section?.id?.replace('shopify-section-', '') || null,
      };
    }

    // Generic fallback: focused element with a stable id (price-range inputs,
    // accordion summaries with ids, etc.).
    if (active.id) return { type: 'by-id', id: active.id };

    return null;
  }

  static restoreFocus(snapshot) {
    if (!snapshot) return;
    let target = null;
    switch (snapshot.type) {
      case 'filter-row': {
        const input = document.getElementById(snapshot.inputId);
        target = input?.closest('li[role="checkbox"]') || input;
        break;
      }
      case 'sort-row': {
        // Prefer the summary after a sort apply so Tab doesn't land back on
        // a menu option (which used to feel like the sort re-opened / re-fired).
        const input = document.querySelector(
          `.facet-sorting-item input[type="radio"][value="${CSS.escape(snapshot.value)}"]`
        );
        const form = input?.closest('[data-facet-sort-form]');
        target = form?.querySelector('summary') || input?.closest('.facet-sorting-item');
        break;
      }
      case 'sort-summary': {
        const form = snapshot.formId
          ? document.getElementById(snapshot.formId)
          : document.querySelector('[data-facet-sort-form]');
        target = form?.querySelector('summary');
        break;
      }
      case 'facet-toggle': {
        target = document.querySelector(
          `[data-facet-toggle][data-section-id="${snapshot.sectionId}"]`
        );
        break;
      }
      case 'coco-facet-remove': {
        // Try the same pill first (filter still active), otherwise the
        // clear-all link, otherwise the section's facet toggle.
        const all = document.querySelectorAll('a[is="coco-facet-remove"]');
        for (const link of all) {
          if (link.getAttribute('href') === snapshot.href) {
            target = link;
            break;
          }
        }
        if (!target) {
          target =
            document.querySelector('[data-facet-filter-clear]') ||
            (snapshot.sectionId &&
              document.querySelector(
                `[data-facet-toggle][data-section-id="${snapshot.sectionId}"]`
              ));
        }
        break;
      }
      case 'by-id': {
        target = document.getElementById(snapshot.id);
        break;
      }
    }
    target?.focus?.({ preventScroll: true });
  }

  // Called before a section re-render. Returns a serializable snapshot.
  static reparentBodyDrawer(sectionId, bodyDrawer) {
    if (!bodyDrawer || bodyDrawer.parentElement !== document.body) return;

    bodyDrawer.releaseModal?.();
    bodyDrawer.releaseModal = null;
    bodyDrawer.removeAttribute('active');
    bodyDrawer.removeAttribute('open');
    bodyDrawer.removeAttribute('data-scroll-locked');

    const host = document.querySelector(
      `#shopify-section-${sectionId} [data-facets-drawer-host]`
    );
    if (host && !host.querySelector(`#${CSS.escape(bodyDrawer.id)}`)) {
      host.appendChild(bodyDrawer);
    } else if (!host?.contains(bodyDrawer)) {
      bodyDrawer.remove();
    }

    CocoCollectionFilters.clearStaleBodyInert();
    unlockScroll();
  }

  static captureState(sectionId) {
    const bodyDrawer = document.body.querySelector(
      `:scope > facets-drawer#${CSS.escape(`facets-drawer-${sectionId}`)}`
    );
    if (bodyDrawer?.hasAttribute('active')) {
      return { drawerOpen: true };
    }
    // Keep the drawer element — reparent to host instead of deleting it.
    if (bodyDrawer) {
      CocoCollectionFilters.reparentBodyDrawer(sectionId, bodyDrawer);
    }
    const filters = document.querySelector(
      `div[is="coco-collection-filters"][data-section-id="${sectionId}"]`
    );
    return {
      drawerOpen: false,
      sidebarState: filters?.getAttribute('data-state') || 'closed',
    };
  }
  static restoreState(sectionId, snapshot) {
    if (!snapshot) return;

    const filters = document.querySelector(
      `div[is="coco-collection-filters"][data-section-id="${sectionId}"]`
    );

    if (snapshot.drawerOpen) {
      const bodyDrawer = document.body.querySelector(
        `:scope > facets-drawer#${CSS.escape(`facets-drawer-${sectionId}`)}`
      );
      const freshDrawer = document.querySelector(
        `#shopify-section-${sectionId} [data-facets-drawer-host] > facets-drawer#${CSS.escape(`facets-drawer-${sectionId}`)}`
      );

      // User may have closed the drawer while the filter request was in flight.
      // Do not re-lock Lenis / recreate the focus trap on a closed drawer.
      if (!bodyDrawer?.hasAttribute('active')) {
        if (bodyDrawer) {
          CocoCollectionFilters.reparentBodyDrawer(sectionId, bodyDrawer);
        }
        filters?.syncTriggerState();
        unlockScroll();
        return;
      }

      // Filter content is patched in CocoFacetFilters.renderFilters — do not
      // replace drawer innerHTML here or scroll/focus jumps to the top.
      // Drop a duplicate host copy only when the live drawer stays on body.
      if (freshDrawer && bodyDrawer) freshDrawer.remove();
      if (bodyDrawer && filters) {
        if (bodyDrawer.releaseModal) {
          filters.releaseDrawerModal = bodyDrawer.releaseModal;
        } else {
          CocoCollectionFilters.clearStaleBodyInert(bodyDrawer);
          bodyDrawer.setAttribute('data-scroll-locked', '');
          lockScroll(bodyDrawer);
          bodyDrawer.releaseModal = activateDrawer(bodyDrawer, {
            initialFocus: (d) =>
              d.querySelector('[is="close-drawer"]:not([data-fixed-overlay])'),
          });
          filters.releaseDrawerModal = bodyDrawer.releaseModal;
        }
      }

      filters?.syncTriggerState();
      return;
    }

    if (filters) {
      if (snapshot.sidebarState === 'open') filters.openSidebar();
      else filters.closeSidebar();
    }
  }

  // ---- Internal -------------------------------------------------------------

  syncTriggerState() {
    const expanded = this.isOpen ? 'true' : 'false';
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded);
    });
  }

  onDocumentClick(event) {
    // Close triggers (X button, overlay, "Show results") inside this section's
    // drawer (body-attached when open) or sidebar. Open is handled directly on
    // the trigger button — see bindTrigger().
    const closer = event.target.closest('[is="close-drawer"]');
    if (!closer) return;

    const drawer = this.drawer;
    if (drawer && closer.closest(`#${CSS.escape(drawer.id)}`)) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    const sidebar = this.sidebar;
    if (sidebar && closer.closest(`#${CSS.escape(sidebar.id)}`)) {
      event.preventDefault();
      this.closeSidebar();
    }
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.close();
  }

  onMediaChange(event) {
    // Crossing to desktop sidebar layout: close the drawer overlay.
    if (event.matches && this.filterStyle === 'sidebar' && this.drawer?.hasAttribute('active')) {
      this.closeDrawer();
    }
    this.syncFilterInert();
  }
}

if (!customElements.get('coco-collection-filters')) {
  customElements.define('coco-collection-filters', CocoCollectionFilters, { extends: 'div' });
}

// =============================================================================
// Filter form / remove anchor / price range — small element wrappers.
// `CocoFacetFilters.renderPage` delegates state preservation to
// CocoCollectionFilters.captureState / restoreState.
// =============================================================================

class CocoFacetFilters extends HTMLElement {
  connectedCallback() {
    CocoFacetFilters.bindSortForm();
  }

  static bindSortForm() {
    document.querySelectorAll('[data-facet-sort-form]').forEach((sortForm) => {
      // Form survives innerHTML swaps; details/options are replaced after each apply.
      if (!sortForm._facetSortBound) {
        sortForm._facetSortBound = true;
        sortForm.removeAttribute('data-bound');

        sortForm.addEventListener('submit', (event) => {
          event.preventDefault();
        });

        // Native radio change must never apply — only applySortRow does.
        sortForm.addEventListener('change', (event) => {
          if (event.target.name !== 'sort_by') return;
          event.stopPropagation();
        });
      }

      const details = sortForm.querySelector('details');
      if (!details) return;

      CocoFacetFilters.initSortRows(details, sortForm);
    });
  }

  /**
   * Keyboard flow:
   * 1. Tab → sort summary
   * 2. Enter / Space → open options (native <details>)
   * 3. ArrowUp / ArrowDown → move focus between options (no apply)
   * 4. Enter / Space / click on option → apply sort
   */
  static initSortRows(details, sortForm) {
    const list = details.querySelector('[data-facet-sorting-menu-list]');
    const rows = Array.from(details.querySelectorAll('[data-facet-sorting-item]'));
    if (!rows.length) return;

    if (list) list.setAttribute('role', 'listbox');

    rows.forEach((row) => {
      const input = row.querySelector('input[type="radio"]');
      if (input) {
        input.tabIndex = -1;
        input.setAttribute('aria-hidden', 'true');
      }
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', input?.checked ? 'true' : 'false');
      row.tabIndex = input?.checked ? 0 : -1;
    });

    if (!rows.some((row) => row.tabIndex === 0)) rows[0].tabIndex = 0;

    // Fresh <details> after each apply — bind once per details node.
    if (details._sortRowsBound) return;
    details._sortRowsBound = true;

    details.addEventListener('toggle', () => {
      if (!details.open) return;
      if (window.matchMedia('(pointer: coarse)').matches) return;
      const target = CocoFacetFilters.getActiveSortRow(details);
      target?.focus?.({ preventScroll: true });
    });

    details.addEventListener('focusout', (event) => {
      if (event.relatedTarget && details.contains(event.relatedTarget)) return;
      requestAnimationFrame(() => {
        if (!details.hasAttribute('open')) return;
        const active = document.activeElement;
        if (details.contains(active)) return;
        if (active === document.body || active === document.documentElement) return;
        CocoFacetFilters.setSortDetailsOpen(details, false);
      });
    });

    details.addEventListener('click', (event) => {
      const row = event.target.closest('[data-facet-sorting-item]');
      if (!row || !details.contains(row)) return;
      event.preventDefault();
      CocoFacetFilters.applySortRow(row, details);
    });

    details.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && details.hasAttribute('open')) {
          event.preventDefault();
          event.stopPropagation();
          CocoFacetFilters.closeSortDetails(sortForm);
          return;
        }

        const row = event.target.closest('[data-facet-sorting-item]');
        if (!row || !details.contains(row)) return;

        const options = Array.from(details.querySelectorAll('[data-facet-sorting-item]'));
        const idx = options.indexOf(row);
        if (idx < 0) return;

        let next = -1;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          next = (idx + 1) % options.length;
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          next = (idx - 1 + options.length) % options.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = options.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          CocoFacetFilters.applySortRow(row, details);
          return;
        }

        if (next < 0) return;
        // Move focus only — do not change the selected sort value.
        event.preventDefault();
        event.stopPropagation();
        options.forEach((option, i) => {
          option.tabIndex = i === next ? 0 : -1;
        });
        options[next].focus({ preventScroll: true });
      },
      true
    );
  }

  static applySortRow(row, details) {
    const input = row.querySelector('input[type="radio"]');
    if (!input) return;

    const form = input.closest('[data-facet-sort-form]');
    const value = input.value;
    const options = Array.from(details.querySelectorAll('[data-facet-sorting-item]'));

    options.forEach((option) => {
      const selected = option === row;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      option.tabIndex = selected ? 0 : -1;
    });

    if (input.checked) {
      CocoFacetFilters.closeSortDetails(form, { animate: false });
      return;
    }

    input.checked = true;
    CocoFacetFilters.closeSortDetails(form, { animate: false });
    CocoFacetFilters.renderSortPage(value);
  }

  // First row that should receive focus when the dropdown opens: the
  // currently-selected one, or the first row otherwise.
  static getActiveSortRow(details) {
    const checked = details.querySelector(
      '.facet-sorting-item input[type="radio"]:checked'
    );
    return checked?.closest('.facet-sorting-item')
      || details.querySelector('[data-facet-sorting-item]');
  }

  // Closes the sort dropdown and returns focus to its summary button so
  // tab order continues from there.
  static closeSortDetails(sortForm, { animate = true } = {}) {
    const forms = sortForm
      ? [sortForm]
      : Array.from(document.querySelectorAll('[data-facet-sort-form]'));

    forms.forEach((form) => {
      const details = form.querySelector('details');
      if (!details) return;
      const wasOpen = details.hasAttribute('open') || details.open === true;
      CocoFacetFilters.setSortDetailsOpen(details, false, { animate });
      if (wasOpen) details.querySelector('summary')?.focus?.({ preventScroll: true });
    });
  }

  static setSortDetailsOpen(details, open, { animate = true } = {}) {
    if (!details) return;

    if (typeof details.setSortOpen === 'function') {
      details.setSortOpen(open, { animate });
      return;
    }

    if (open) details.setAttribute('open', '');
    else details.removeAttribute('open');
  }

  static getSectionId() {
    return (
      document.querySelector('form[is="coco-facet-form"]')?.dataset.sectionId ||
      document.querySelector('[data-facet-sort-form]')?.dataset.sectionId ||
      document.querySelector('[data-facets-drawer-host]')?.dataset.sectionId ||
      document.querySelector('#CocoFacetFiltersContainer')?.dataset.sectionId ||
      document.querySelector('[data-main-collection-section]')?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') ||
      document.querySelector('[data-main-search-section]')?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '')
    );
  }

  static isSearchPage() {
    return (
      window.location.pathname.endsWith('/search') ||
      window.location.pathname.includes('/search/') ||
      new URLSearchParams(window.location.search).has('q')
    );
  }

  static mergeSearchContextParams(params) {
    const currentParams = new URLSearchParams(window.location.search);

    if (!CocoFacetFilters.isSearchPage() && !params.has('q') && !currentParams.has('q')) {
      return params;
    }

    if (!params.has('q') && currentParams.has('q')) {
      params.set('q', currentParams.get('q'));
    }

    if (!params.has('type') && currentParams.has('type')) {
      params.set('type', currentParams.get('type'));
    } else if (!params.has('type')) {
      params.set('type', 'product');
    }

    if (!params.has('options[prefix]') && currentParams.has('options[prefix]')) {
      params.set('options[prefix]', currentParams.get('options[prefix]'));
    } else if (!params.has('options[prefix]')) {
      params.set('options[prefix]', 'last');
    }

    return params;
  }

  static normalizePageParams(searchParams, resetPage = true) {
    const params = new URLSearchParams(searchParams);
    const currentParams = new URLSearchParams(window.location.search);

    params.delete('section_id');
    if (resetPage) params.delete('page');

    CocoFacetFilters.mergeSearchContextParams(params);

    if (!params.has('sort_by') && currentParams.has('sort_by')) {
      params.set('sort_by', currentParams.get('sort_by'));
    }

    return params;
  }

  static createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    window.history.pushState({ searchParams }, '', url);
  }

  static parseSection(responseText, sectionId) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    return html.getElementById(`shopify-section-${sectionId}`);
  }

  static hasActiveFilterParams(searchParams = '') {
    const params = typeof searchParams === 'string' ? searchParams : String(searchParams);
    return /(?:^|&)filter\./.test(params);
  }

  static getFilterRoots(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    const bodyDrawer = document.body.querySelector(
      `:scope > facets-drawer#${CSS.escape(`facets-drawer-${sectionId}`)}`
    );
    return [section, bodyDrawer].filter(Boolean);
  }

  static getSourceFilterForm(section, sectionId) {
    return (
      section.querySelector(`[data-facets-drawer-host] form[is="coco-facet-form"]`) ||
      section.querySelector(`#facets-drawer-filters-${sectionId} form[is="coco-facet-form"]`) ||
      section.querySelector(`#facets-sidebar-${sectionId} form[is="coco-facet-form"]`) ||
      section.querySelector('form[is="coco-facet-form"]')
    );
  }

  static matchesActiveFilter(element, event) {
    const activeFilter = event?.target?.closest?.('[data-filter]');
    if (!activeFilter) return false;

    if (element.dataset.index && activeFilter.dataset.index) {
      return element.dataset.index === activeFilter.dataset.index;
    }

    return element.dataset.filter === activeFilter.dataset.filter;
  }

  static getFilterSelector(sourceFilter) {
    const parts = [`[data-filter="${CSS.escape(sourceFilter.dataset.filter)}"]`];
    if (sourceFilter.dataset.index) {
      parts.push(`[data-index="${CSS.escape(sourceFilter.dataset.index)}"]`);
    }
    return parts.join('');
  }

  static patchFilterElement(target, source) {
    if (target.tagName === 'DETAILS') {
      const targetContent =
        target.querySelector('.accordion-details-content') || target.querySelector('summary + *');
      const sourceContent =
        source.querySelector('.accordion-details-content') || source.querySelector('summary + *');
      if (targetContent && sourceContent) {
        targetContent.innerHTML = sourceContent.innerHTML;
      }
      return;
    }

    target.innerHTML = source.innerHTML;
  }

  static renderFilters(responseText, event, sectionId) {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const sourceForm = section && CocoFacetFilters.getSourceFilterForm(section, sectionId);
    if (!sourceForm) return;

    const facetElements = sourceForm.querySelectorAll('[data-filter]');
    const facetsToRender = Array.from(facetElements).filter(
      (element) => !CocoFacetFilters.matchesActiveFilter(element, event)
    );

    facetsToRender.forEach((sourceFilter) => {
      const selector = CocoFacetFilters.getFilterSelector(sourceFilter);
      CocoFacetFilters.getFilterRoots(sectionId).forEach((root) => {
        root.querySelectorAll(selector).forEach((target) => {
          CocoFacetFilters.patchFilterElement(target, sourceFilter);
        });
      });
    });
  }

  static renderFiltersActive(responseText, sectionId, searchParams = '') {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const destination = document.getElementById(`shopify-section-${sectionId}`);
    if (!section || !destination) return;

    const srcActive = section.querySelector('.facets-active');
    const destActive = destination.querySelector('.facets-active');
    const wrapper = destination.querySelector('.collection-content-wrapper');

    if (srcActive && destActive) {
      destActive.innerHTML = srcActive.innerHTML;
    } else if (srcActive && !destActive && wrapper) {
      wrapper.insertAdjacentElement('beforebegin', srcActive.cloneNode(true));
    } else if (!srcActive && destActive) {
      // Use incoming params — URL is updated after this runs (clear-all fix).
      if (!CocoFacetFilters.hasActiveFilterParams(searchParams)) {
        destActive.remove();
      }
    }
  }

  static renderProductGrid(responseText, sectionId) {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const destination = document.getElementById(`shopify-section-${sectionId}`);
    const srcProducts = section?.querySelector('.products-list-container');
    const destProducts = destination?.querySelector('.products-list-container');
    if (srcProducts && destProducts) {
      destProducts.innerHTML = srcProducts.innerHTML;
    }
  }

  static renderProductCount(responseText, sectionId) {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const destination = document.getElementById(`shopify-section-${sectionId}`);
    const srcCount = section?.querySelector('[data-facet-product-count]');
    const destCount = destination?.querySelector('[data-facet-product-count]');
    if (srcCount && destCount) destCount.innerHTML = srcCount.innerHTML;
  }

  static renderSortBy(responseText, sectionId) {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const destination = document.getElementById(`shopify-section-${sectionId}`);
    if (!section || !destination) return;

    const srcSort = section.querySelector('[data-facet-sort-form]');
    const destSort = destination.querySelector('[data-facet-sort-form]');
    if (srcSort && destSort) destSort.innerHTML = srcSort.innerHTML;
  }

  static getFilterCountTargets(sectionId) {
    const targets = new Set();
    const section = document.getElementById(`shopify-section-${sectionId}`);

    section
      ?.querySelectorAll(
        `#facets-sidebar-${sectionId} [data-facet-filter-products-count], #CocoFacetFiltersContainer [data-facet-filter-products-count], [data-facets-drawer-host] [data-facet-filter-products-count]`
      )
      .forEach((el) => targets.add(el));

    document
      .getElementById(`facets-drawer-${sectionId}`)
      ?.querySelectorAll('[data-facet-filter-products-count]')
      .forEach((el) => targets.add(el));

    return Array.from(targets);
  }

  static renderFilterProductsCount(responseText, sectionId) {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    if (!section) return;

    const srcCount =
      section.querySelector(`#facets-drawer-${sectionId} [data-facet-filter-products-count]`) ||
      section.querySelector(`#facets-sidebar-${sectionId} [data-facet-filter-products-count]`) ||
      section.querySelector(`#facets-drawer-filters-${sectionId} [data-facet-filter-products-count]`) ||
      section.querySelector('[data-facet-filter-products-count]');
    if (!srcCount) return;

    CocoFacetFilters.getFilterCountTargets(sectionId).forEach((dest) => {
      dest.innerHTML = srcCount.innerHTML;
    });
  }

  static renderDrawerMeta(responseText, sectionId) {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const srcDrawer = section?.querySelector(`#facets-drawer-${sectionId}`);
    if (!srcDrawer) return;

    const srcBtn = srcDrawer.querySelector('.facet-filters-button');
    if (!srcBtn) return;

    const drawer = document.getElementById(`facets-drawer-${sectionId}`);
    const destBtn = drawer?.querySelector('.facet-filters-button');
    if (destBtn) destBtn.textContent = srcBtn.textContent;
  }

  static renderFacetToolbar(responseText, sectionId, searchParams = '') {
    const section = CocoFacetFilters.parseSection(responseText, sectionId);
    const destination = document.getElementById(`shopify-section-${sectionId}`);
    if (!section || !destination) return;

    const srcToolbar = section.querySelector('.facet-toolbar');
    const destToolbar = destination.querySelector('.facet-toolbar');
    const wrapper = destination.querySelector('.collection-content-wrapper');

    // Count + sort are patched elsewhere; only insert/remove the toolbar shell
    // so [data-facet-toggle] buttons keep their click listeners.
    if (srcToolbar && !destToolbar && wrapper) {
      wrapper.insertAdjacentElement('beforebegin', srcToolbar.cloneNode(true));
      CocoCollectionFilters.rebindTriggersForSection(sectionId);
    } else if (!srcToolbar && destToolbar) {
      if (!CocoFacetFilters.hasActiveFilterParams(searchParams)) {
        destToolbar.remove();
      }
    }
  }

  static reinitializeForms(sectionId) {
    const forms = new Set();
    document
      .querySelectorAll(`#shopify-section-${sectionId} form[is="coco-facet-form"]`)
      .forEach((form) => forms.add(form));

    const bodyForm = document.body.querySelector(
      `:scope > facets-drawer#${CSS.escape(`facets-drawer-${sectionId}`)} form[is="coco-facet-form"]`
    );
    if (bodyForm) forms.add(bodyForm);

    forms.forEach((form) => {
      if (typeof form.initCheckboxRows === 'function') form.initCheckboxRows();
    });
  }

  static scrollToProductGrid(sectionId, { drawerOpen = false } = {}) {
    if (drawerOpen) return;

    const section = document.getElementById(`shopify-section-${sectionId}`);
    const grid =
      section?.querySelector('.section-spacing') ||
      section?.querySelector('.products-list-container') ||
      section?.querySelector('.collection-content');

    if (!grid) return;

    const styles = getComputedStyle(document.documentElement);
    const headerOffset =
      parseFloat(styles.getPropertyValue('--stickyheaderHeight')) ||
      parseFloat(styles.getPropertyValue('--dynamicAnnouncementHeight')) ||
      0;
    const layoutOffset = MOBILE_FILTER_MQ.matches ? 95 : 20;
    const top = Math.max(0, grid.getBoundingClientRect().top + window.scrollY - headerOffset - layoutOffset);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    if (window.lenis?.scrollTo) {
      window.lenis.scrollTo(top, { behavior });
    } else {
      window.scrollTo({ top, behavior });
    }
  }

  static renderPage(searchParams, event, updateURLHash = true, resetPage = true) {
    const sectionId = CocoFacetFilters.getSectionId();
    if (!sectionId) return;

    const snapshot = CocoCollectionFilters.captureState(sectionId);
    const focusSnapshot = CocoCollectionFilters.captureFocus();
    const openAccordions = CocoCollectionFilters.captureOpenAccordions();

    const params = CocoFacetFilters.normalizePageParams(searchParams, resetPage);
    const normalizedParams = params.toString();

    const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}${normalizedParams ? `&${normalizedParams}` : ''}`;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        CocoFacetFilters.renderFilters(responseText, event, sectionId);
        CocoFacetFilters.renderFiltersActive(responseText, sectionId, normalizedParams);
        CocoFacetFilters.renderProductGrid(responseText, sectionId);
        CocoFacetFilters.renderProductCount(responseText, sectionId);
        CocoFacetFilters.renderSortBy(responseText, sectionId);
        CocoFacetFilters.renderFilterProductsCount(responseText, sectionId);
        CocoFacetFilters.renderDrawerMeta(responseText, sectionId);
        CocoFacetFilters.renderFacetToolbar(responseText, sectionId, normalizedParams);
        CocoFacetFilters.reinitializeForms(sectionId);

        CocoCollectionFilters.restoreState(sectionId, snapshot);
        CocoCollectionFilters.rebindTriggersForSection(sectionId);
        CocoFacetFilters.bindSortForm();
        CocoCollectionFilters.restoreOpenAccordions(openAccordions);
        CocoCollectionFilters.restoreFocus(focusSnapshot);

        if (updateURLHash) CocoFacetFilters.updateURLHash(normalizedParams);

        window.dispatchEvent(
          new CustomEvent('shopify:section:rendered', { detail: { sectionId } })
        );

        CocoFacetFilters.scrollToProductGrid(sectionId, { drawerOpen: snapshot.drawerOpen });
      })
      .catch((error) => console.error(error));
  }

  static renderSortPage(sortValue) {
    const sectionId = CocoFacetFilters.getSectionId();
    if (!sectionId) return;

    const params = CocoFacetFilters.normalizePageParams(window.location.search.slice(1), true);
    params.set('sort_by', sortValue);

    CocoFacetFilters.renderPage(params.toString(), null);
  }

  get sortByDetails() {
    return this.querySelector('[is="sort-by-details"]');
  }
}

class CocoFacetForm extends HTMLFormElement {
  constructor() {
    super();
    this.debouncedSubmit = debounce((event) => this.handleSubmit(event), DEBOUNCE_MS);
    this.onRowKeydown = this.onRowKeydown.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('input', this.debouncedSubmit);
    this.addEventListener('change', this.debouncedSubmit);

    // Make each filter <li> the keyboard + focus target instead of the small
    // <input class="checkbox"> inside it: the whole row becomes a single tab
    // stop with a visible focus ring, Enter / Space toggles it, and the row
    // gets ARIA semantics so screen readers see it as a checkbox.
    this.initCheckboxRows();
    this.addEventListener('keydown', this.onRowKeydown);
    this.addEventListener('click', this.onRowClick);
    this.addEventListener('change', this.onCheckboxChange);
  }

  initCheckboxRows() {
    this.querySelectorAll('li').forEach((row) => {
      const input = row.querySelector('input[type="checkbox"]');
      if (!input) return;
      row.setAttribute('role', 'checkbox');
      row.setAttribute('aria-checked', input.checked ? 'true' : 'false');
      if (input.disabled) {
        row.setAttribute('aria-disabled', 'true');
        row.tabIndex = -1;
      } else {
        row.removeAttribute('aria-disabled');
        row.tabIndex = 0;
      }
      // Take the native checkbox out of the tab order so the row is the only
      // stop. Native click on the checkbox / its <label for> still works.
      input.tabIndex = -1;
    });
  }

  onRowKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target.closest('li[role="checkbox"]');
    if (!row || !this.contains(row)) return;
    // Don't hijack typing inside inputs / textareas nested in the row
    // (e.g. the price-range numeric fields).
    const tag = event.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const input = row.querySelector('input[type="checkbox"]');
    if (!input || input.disabled) return;
    event.preventDefault();
    input.click();
  }

  onRowClick(event) {
    // Clicks on the input or the <label for> already toggle natively. We only
    // need to forward clicks that land on the bare <li> (gap / padding).
    const row = event.target.closest('li[role="checkbox"]');
    if (!row || !this.contains(row)) return;
    if (event.target.closest('label, input')) return;
    const input = row.querySelector('input[type="checkbox"]');
    if (!input || input.disabled) return;
    input.click();
  }

  onCheckboxChange(event) {
    const input = event.target;
    if (!input || input.type !== 'checkbox') return;
    const row = input.closest('li[role="checkbox"]');
    if (row) row.setAttribute('aria-checked', input.checked ? 'true' : 'false');
  }

  handleSubmit(event) {
    event.preventDefault();
    const params = CocoFacetFilters.normalizePageParams(
      CocoFacetFilters.createSearchParams(this),
      true
    );
    CocoFacetFilters.renderPage(params.toString(), event);
  }
}

class CocoFacetRemove extends HTMLAnchorElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  onClick = (event) => {
    event.preventDefault();

    const form = this.closest('form[is="coco-facet-form"]') || document.querySelector('form[is="coco-facet-form"]');
    if (!form) {
      window.location.href = this.href;
      return;
    }

    const url = new URL(this.href, window.location.origin);
    const params = CocoFacetFilters.normalizePageParams(url.search.slice(1), true);
    CocoFacetFilters.renderPage(params.toString(), event);
  };
}

class CocoPriceRange extends HTMLElement {
  connectedCallback() {
    this.minRange = this.querySelector('[data-min-range]');
    this.maxRange = this.querySelector('[data-max-range]');
    this.minInput = this.querySelector('input[name*="filter.v.price.gte"]');
    this.maxInput = this.querySelector('input[name*="filter.v.price.lte"]');
    this.wrapper = this.querySelector('[data-range-wrapper]');

    this.minRange?.addEventListener('input', () => this.onRangeInput());
    this.maxRange?.addEventListener('input', () => this.onRangeInput());
    this.minInput?.addEventListener('change', () => this.onInputChange());
    this.maxInput?.addEventListener('change', () => this.onInputChange());
  }

  onRangeInput() {
    if (!this.minRange || !this.maxRange) return;
    let minValue = Number(this.minRange.value);
    let maxValue = Number(this.maxRange.value);
    if (minValue > maxValue) [minValue, maxValue] = [maxValue, minValue];
    this.minRange.value = minValue;
    this.maxRange.value = maxValue;
    if (this.minInput) this.minInput.value = minValue;
    if (this.maxInput) this.maxInput.value = maxValue;
    this.updateProgress(minValue, maxValue);
  }

  onInputChange() {
    if (!this.minRange || !this.maxRange || !this.minInput || !this.maxInput) return;
    let minValue = Number(this.minInput.value || this.minRange.min);
    let maxValue = Number(this.maxInput.value || this.maxRange.max);
    if (minValue > maxValue) [minValue, maxValue] = [maxValue, minValue];
    this.minRange.value = minValue;
    this.maxRange.value = maxValue;
    this.minInput.value = minValue;
    this.maxInput.value = maxValue;
    this.updateProgress(minValue, maxValue);
  }

  updateProgress(minValue, maxValue) {
    if (!this.wrapper || !this.maxRange) return;
    const max = Number(this.maxRange.max) || 1;
    this.wrapper.style.setProperty('--range-min', `${(minValue / max) * 100}%`);
    this.wrapper.style.setProperty('--range-max', `${(maxValue / max) * 100}%`);
  }
}


if (!customElements.get('coco-facet-filters')) customElements.define('coco-facet-filters', CocoFacetFilters);
if (!customElements.get('coco-facet-form')) customElements.define('coco-facet-form', CocoFacetForm, { extends: 'form' });
if (!customElements.get('coco-facet-remove')) customElements.define('coco-facet-remove', CocoFacetRemove, { extends: 'a' });
if (!customElements.get('coco-price-range')) customElements.define('coco-price-range', CocoPriceRange);

// =============================================================================
// <coco-facet-sticky> — mobile Filter & sort bar. Fixed at the bottom; shown
// while the collection is on screen, and hidden only once the section bottom
// reaches the viewport bottom (end of the collection).
// =============================================================================
class CocoFacetSticky extends HTMLElement {
  connectedCallback() {
    this.root =
      this.closest('.collection-content') ||
      this.closest('[data-main-collection-section]') ||
      this.closest('.shopify-section');

    this._inView = false;
    this._visible = false;
    this._scrollCleanup = null;
    this.onMediaChange = this.onMediaChange.bind(this);
    this.updateInView = this.updateInView.bind(this);
    this.onScroll = throttle(this.updateInView, 50);

    MOBILE_FILTER_MQ.addEventListener?.('change', this.onMediaChange);
    this.bindScroll();
    this.updateInView();
  }

  disconnectedCallback() {
    MOBILE_FILTER_MQ.removeEventListener?.('change', this.onMediaChange);
    this.unbindScroll();
  }

  get button() {
    return this.querySelector('.button');
  }

  bindScroll() {
    this.unbindScroll();

    const cleanup = [];

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    cleanup.push(() => {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    });

    const bindLenis = () => {
      if (!window.lenis?.on) return false;
      window.lenis.on('scroll', this.onScroll);
      cleanup.push(() => window.lenis?.off?.('scroll', this.onScroll));
      return true;
    };

    if (!bindLenis()) {
      const timer = window.setInterval(() => {
        if (!bindLenis()) return;
        clearInterval(timer);
      }, 100);
      cleanup.push(() => clearInterval(timer));
    }

    this._scrollCleanup = () => {
      cleanup.forEach((release) => release());
      this._scrollCleanup = null;
    };
  }

  unbindScroll() {
    this._scrollCleanup?.();
    this._scrollCleanup = null;
  }

  onMediaChange() {
    this.updateInView();
  }

  updateInView() {
    if (!this.root || !MOBILE_FILTER_MQ.matches) {
      this._inView = false;
      this.syncVisibility(false);
      return;
    }

    const rect = this.root.getBoundingClientRect();
    const viewportBottom = window.innerHeight || 0;

    // Show once the section has entered the viewport, and keep showing until
    // the section's bottom edge reaches the viewport bottom.
    // Short sections (shorter than the viewport): show while still on screen.
    const hasEntered = rect.top < viewportBottom;
    const bottomStillBelowViewport = rect.bottom > viewportBottom;
    const shortSectionOnScreen =
      rect.height <= viewportBottom && rect.bottom > 0 && rect.top < viewportBottom;

    this._inView = hasEntered && (bottomStillBelowViewport || shortSectionOnScreen);
    this.syncVisibility(true);
  }

  syncVisibility(animate) {
    const show = MOBILE_FILTER_MQ.matches && this._inView;
    if (show === this._visible && animate) return;
    this._visible = show;
    this.setAttribute('aria-hidden', show ? 'false' : 'true');

    const button = this.button;
    if (!button) return;

    const reducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !window.Motion?.animate;

    if (!animate || reducedMotion) {
      button.style.opacity = show ? '1' : '0';
      button.style.visibility = show ? 'visible' : 'hidden';
      button.style.transform = show ? 'translateY(0)' : 'translateY(15px)';
      return;
    }

    if (show) {
      window.Motion.animate(
        button,
        {
          opacity: 1,
          visibility: 'visible',
          transform: ['translateY(15px)', 'translateY(0)'],
        },
        {
          duration: 0.45,
          easing: [0.16, 1, 0.3, 1],
        }
      );
    } else {
      window.Motion.animate(
        button,
        {
          opacity: 0,
          visibility: 'hidden',
          transform: ['translateY(0)', 'translateY(15px)'],
        },
        {
          duration: 0.35,
          easing: [0.16, 1, 0.3, 1],
        }
      );
    }
  }
}

if (!customElements.get('coco-facet-sticky')) customElements.define('coco-facet-sticky', CocoFacetSticky);

// =============================================================================
// <coco-facet-toolbar> — sticky filter/sort bar. Adds .sticky-scrolled to
// .facet-toolbar while the wrapper is stuck under the sticky header.
// =============================================================================
class CocoFacetToolbar extends HTMLElement {
  connectedCallback() {
    this.toolbar = this.querySelector('.facet-toolbar');
    this.updateStuck = this.updateStuck.bind(this);
    this.onScroll = throttle(this.updateStuck, 50);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    document.addEventListener('header:scrolled', this.updateStuck);
    document.addEventListener('announcement:height', this.updateStuck);

    requestAnimationFrame(this.updateStuck);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('header:scrolled', this.updateStuck);
    document.removeEventListener('announcement:height', this.updateStuck);
  }

  updateStuck() {
    if (!this.toolbar) {
      this.toolbar = this.querySelector('.facet-toolbar');
      if (!this.toolbar) return;
    }

    const stickyTop = parseFloat(getComputedStyle(this).top) || 0;
    const rect = this.getBoundingClientRect();
    const isStuck = rect.top <= stickyTop + 1 && rect.bottom > stickyTop;

    this.toolbar.classList.toggle('sticky-scrolled', isStuck);
  }
}

if (!customElements.get('coco-facet-toolbar')) {
  customElements.define('coco-facet-toolbar', CocoFacetToolbar);
}

window.addEventListener('popstate', () => {
  CocoFacetFilters.renderPage(window.location.search.slice(1), null, false, false);
});

// =============================================================================
// <details is="sort-by-details"> — Motion height fill open/close for sort menu
// =============================================================================
const SORT_EASING = [0.16, 1, 0.3, 1];
const SORT_OPEN_DURATION = 0.38;
const SORT_CLOSE_DURATION = 0.28;

class SortByDetails extends HTMLDetailsElement {
  constructor() {
    super();
    this._open = this.hasAttribute('open');
    this.transitionToken = 0;
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('.facet-sorting-menu');
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.sortingFilterElement = this.closest('coco-facet-filters');
  }

  connectedCallback() {
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('.facet-sorting-menu');
    this.summaryElement?.addEventListener('click', this.onSummaryClick);
    this.summaryElement?.setAttribute('aria-expanded', this._open ? 'true' : 'false');
    if (!this._open) this.resetMenuStyles();
  }

  disconnectedCallback() {
    this.summaryElement?.removeEventListener('click', this.onSummaryClick);
  }

  get open() {
    return this._open;
  }

  set open(value) {
    this.setSortOpen(Boolean(value), { animate: true });
  }

  setSortOpen(value, { animate = true } = {}) {
    value = Boolean(value);
    if (value === this._open && (value ? this.hasAttribute('open') : !this.hasAttribute('open'))) {
      return;
    }

    this._open = value;
    this.summaryElement?.setAttribute('aria-expanded', value ? 'true' : 'false');

    if (!this.isConnected) {
      if (value) this.setAttribute('open', '');
      else this.removeAttribute('open');
      return;
    }

    this.transition(value, animate);
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.setSortOpen(!this._open, { animate: true });
  }

  async transition(value, animate = true) {
    const token = ++this.transitionToken;
    const menu = this.contentElement || this.querySelector('.facet-sorting-menu');
    this.contentElement = menu;

    const reducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !window.Motion?.animate ||
      !animate ||
      !menu;

    if (value) {
      this.setAttribute('open', '');
      if (reducedMotion) {
        this.resetMenuStyles();
        this.setSortingHostActive(true, menu?.scrollHeight);
        return;
      }
      await this.showWithMotion(menu);
      if (token !== this.transitionToken) return;
      return;
    }

    if (reducedMotion) {
      this.removeAttribute('open');
      this.resetMenuStyles();
      this.setSortingHostActive(false, 0);
      return;
    }

    await this.hideWithMotion(menu);
    if (token !== this.transitionToken) return;
    if (!this._open) {
      this.removeAttribute('open');
      this.resetMenuStyles();
      this.setSortingHostActive(false, 0);
    }
  }

  async showWithMotion(menu) {
    const height = menu.scrollHeight;
    menu.style.overflow = 'hidden';
    menu.style.height = '0px';
    menu.style.opacity = '0';
    // Force layout so Motion starts from the closed height.
    void menu.offsetHeight;
    this.setSortingHostActive(false, height);

    await window.Motion.animate(
      menu,
      {
        height: ['0px', `${height}px`],
        opacity: [0, 1],
      },
      {
        duration: SORT_OPEN_DURATION,
        easing: SORT_EASING,
      }
    ).finished;

    if (this._open) {
      menu.style.height = 'auto';
      menu.style.overflow = 'hidden';
      menu.style.opacity = '1';
      this.setSortingHostActive(true);
    }
  }

  async hideWithMotion(menu) {
    const height = menu.scrollHeight || menu.getBoundingClientRect().height;
    menu.style.overflow = 'hidden';
    menu.style.height = `${height}px`;
    menu.style.opacity = '1';
    void menu.offsetHeight;
    this.setSortingHostActive(false, height);
    await window.Motion.animate(
      menu,
      {
        height: [`${height}px`, '0px'],
        opacity: [1, 0],
      },
      {
        duration: SORT_CLOSE_DURATION,
        easing: SORT_EASING,
      }
    ).finished;
  }

  resetMenuStyles() {
    const menu = this.contentElement || this.querySelector('.facet-sorting-menu');
    if (!menu) return;
    menu.style.removeProperty('height');
    menu.style.removeProperty('opacity');
    menu.style.removeProperty('overflow');
  }

  setSortingHostActive(active, height) {
    const host = this.sortingFilterElement || this.closest('coco-facet-filters');
    this.sortingFilterElement = host;
    if (!host) return;
    host.classList.toggle('active', Boolean(active));
    if (height != null) {
      host.style.setProperty('--sortingHeight', `${height}px`);
    }
  }
}

if (!customElements.get('sort-by-details')) {
  customElements.define('sort-by-details', SortByDetails, { extends: 'details' });
}

// Click outside any sort dropdown closes it.
document.addEventListener('click', (event) => {
  document.querySelectorAll('[data-facet-sort-form] details[open]').forEach((details) => {
    if (!details.contains(event.target)) {
      CocoFacetFilters.setSortDetailsOpen(details, false);
    }
  });
});

CocoFacetFilters.bindSortForm();

if (!customElements.get('show-more-button')) {
  customElements.define(
    'show-more-button',
    class ShowMoreButton extends HTMLElement {
      constructor() {
        super();
        const button = this.querySelector('button');
        button.addEventListener('click', (event) => {
          this.expandShowMore(event);
          const nextElementToFocus = event.target.closest('.accordion-details-content').querySelector('.show-more-item');
          if (nextElementToFocus && !nextElementToFocus.classList.contains('hidden') && nextElementToFocus.querySelector('input')) {
            nextElementToFocus.querySelector('input').focus();
          }
        });
      }
      expandShowMore(event) {
        const parentDisplay = event.target.closest('[id^="Show-More-"]').closest('.accordion-details-content');
        this.querySelectorAll('.label-text').forEach((element) => element.classList.toggle('hidden'));
        parentDisplay.querySelectorAll('.show-more-item').forEach((item) => item.classList.toggle('hidden'));
        if (!this.querySelector('.label-show-less')) {
          this.classList.add('hidden');
        }
      }
    }
  );
}
